import { useState, useRef, useCallback, useEffect } from 'react'
import { useSettings } from '../contexts/SettingsContext'
import { useChat } from '../contexts/ChatContext'

export const useVoiceChat = () => {
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [transcribedText, setTranscribedText] = useState('')
  const [voiceLevel, setVoiceLevel] = useState(0)
  const [error, setError] = useState(null)
  const { settings } = useSettings()
  const { detectedLanguage } = useChat()
  
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const currentAudioRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const animationFrameRef = useRef(null)
  const streamRef = useRef(null)
  const silenceTimerRef = useRef(null)
  const voiceDetectedRef = useRef(false)
  const recordingStartTimeRef = useRef(null)
  const isStoppedByUserRef = useRef(false)
  const isCleaningUpRef = useRef(false)
  const speechSynthesisUtteranceRef = useRef(null)
  
  const elevenLabsApiKey = import.meta.env.VITE_ELEVENLABS_API_KEY
  
  // Global voice management - stop all voice activities
  const stopAllVoiceActivities = useCallback(() => {
    console.log('🛑 STOPPING ALL VOICE ACTIVITIES (navigation/cleanup)')
    
    // Prevent multiple cleanup calls
    if (isCleaningUpRef.current) {
      console.log('Already cleaning up, skipping...')
      return
    }
    isCleaningUpRef.current = true
    
    try {
      // 1. Stop any currently playing ElevenLabs audio
      if (currentAudioRef.current) {
        console.log('Stopping ElevenLabs audio')
        currentAudioRef.current.pause()
        currentAudioRef.current.currentTime = 0
        currentAudioRef.current.src = ''
        currentAudioRef.current = null
      }
      
      // 2. Stop browser speech synthesis completely
      if (window.speechSynthesis) {
        console.log('Canceling speech synthesis')
        window.speechSynthesis.cancel()
        
        // Clear any pending utterances
        if (speechSynthesisUtteranceRef.current) {
          speechSynthesisUtteranceRef.current.onend = null
          speechSynthesisUtteranceRef.current.onerror = null
          speechSynthesisUtteranceRef.current = null
        }
      }
      
      // 3. Stop listening immediately
      if (isListening) {
        console.log('Stopping listening')
        stopListening(true)
      }
      
      // 4. Reset all states
      setIsSpeaking(false)
      setIsProcessing(false)
      setError(null)
      setVoiceLevel(0)
      
      console.log('✅ All voice activities stopped')
    } catch (error) {
      console.error('Error in stopAllVoiceActivities:', error)
    } finally {
      isCleaningUpRef.current = false
    }
  }, [isListening])
  
  // Voice activity detection with real-time level monitoring
  const startVoiceDetection = useCallback(() => {
    if (!analyserRef.current || !isListening) return
    
    const analyser = analyserRef.current
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    
    const detectVoice = () => {
      // Check if we should continue
      if (!analyser || !isListening || isCleaningUpRef.current) {
        return
      }
      
      try {
        analyser.getByteFrequencyData(dataArray)
        
        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength
        setVoiceLevel(average)
        
        // Voice detection threshold (much more sensitive)
        const voiceThreshold = 15
        const hasVoice = average > voiceThreshold
        
        if (hasVoice && !voiceDetectedRef.current) {
          voiceDetectedRef.current = true
          console.log('Voice detected, recording...')
          
          // Clear any existing silence timer
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current)
            silenceTimerRef.current = null
          }
        } else if (!hasVoice && voiceDetectedRef.current && !isStoppedByUserRef.current) {
          // Start silence timer only if not stopped by user
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              console.log('Silence detected, auto-stopping recording...')
              stopListening()
            }, 1500) // Increased to 1.5 seconds of silence
          }
        }
        
        if (isListening && !isCleaningUpRef.current) {
          animationFrameRef.current = requestAnimationFrame(detectVoice)
        }
      } catch (error) {
        console.error('Error in voice detection:', error)
        setError('Voice detection error')
      }
    }
    
    detectVoice()
  }, [isListening])
  
  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up voice chat resources...')
    
    if (isCleaningUpRef.current) return
    isCleaningUpRef.current = true
    
    try {
      // Clear timers
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      
      // Stop media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop()
          console.log('Stopped track:', track.kind)
        })
        streamRef.current = null
      }
      
      // Close audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(err => {
          console.warn('Error closing audio context:', err)
        })
        audioContextRef.current = null
      }
      
      // Reset refs and state
      analyserRef.current = null
      audioChunksRef.current = []
      voiceDetectedRef.current = false
      isStoppedByUserRef.current = false
      recordingStartTimeRef.current = null
      
      setVoiceLevel(0)
      setIsListening(false)
      setIsProcessing(false)
      setError(null)
    } catch (error) {
      console.error('Error during cleanup:', error)
    } finally {
      isCleaningUpRef.current = false
    }
  }, [])
  
  // Start listening with improved audio setup and error handling
  const startListening = useCallback(async () => {
    if (isListening || isProcessing || isSpeaking || isCleaningUpRef.current) {
      console.log('Cannot start listening - already active or cleaning up')
      return
    }
    
    try {
      console.log('🎤 Starting voice recording...')
      setError(null)
      
      // CRITICAL: Stop any ongoing speech first to prevent echo
      console.log('Stopping all voice activities before starting listening...')
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
        currentAudioRef.current.currentTime = 0
        currentAudioRef.current = null
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
      setIsSpeaking(false) // Ensure speaking state is false
      
      // Reset user stop flag
      isStoppedByUserRef.current = false
      
      // Check for microphone permission first
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' })
      if (permissionStatus.state === 'denied') {
        throw new Error('Microphone permission denied. Please allow microphone access.')
      }
      
      // Request high-quality audio with better error handling
      const constraints = {
        audio: {
          sampleRate: { ideal: 48000, min: 16000 },
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true, // Enable noise suppression for better quality
          autoGainControl: true,
          latency: { ideal: 0.01, max: 0.1 }
        }
      }
      
      console.log('Requesting microphone access...')
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      
      if (!stream || stream.getTracks().length === 0) {
        throw new Error('Failed to get audio stream')
      }
      
      streamRef.current = stream
      console.log('Microphone access granted, setting up audio processing...')
      
      // Setup audio context for voice detection with better error handling
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 48000,
          latencyHint: 'interactive'
        })
        
        // Resume audio context if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume()
        }
        
        audioContextRef.current = audioContext
        
        const source = audioContext.createMediaStreamSource(stream)
        const analyser = audioContext.createAnalyser()
        analyserRef.current = analyser
        
        // Optimized for voice detection
        analyser.fftSize = 512 // Increased for better frequency resolution
        analyser.smoothingTimeConstant = 0.3
        analyser.minDecibels = -90
        analyser.maxDecibels = -10
        
        source.connect(analyser)
        console.log('Audio analysis setup complete')
      } catch (audioError) {
        console.error('Audio context setup error:', audioError)
        throw new Error('Failed to setup audio processing')
      }
      
      // Setup MediaRecorder with better format detection and error handling
      let mimeType = null
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/mpeg',
        'audio/wav'
      ]
      
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type
          console.log('Using MIME type:', mimeType)
          break
        }
      }
      
      if (!mimeType) {
        console.warn('No supported MIME type found, using default')
      }
      
      const mediaRecorderOptions = {
        audioBitsPerSecond: 128000 // Higher quality audio
      }
      
      if (mimeType) {
        mediaRecorderOptions.mimeType = mimeType
      }
      
      const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions)
      mediaRecorderRef.current = mediaRecorder
      
      // Reset state
      audioChunksRef.current = []
      setTranscribedText('')
      voiceDetectedRef.current = false
      recordingStartTimeRef.current = Date.now()
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          console.log('Audio chunk received:', event.data.size, 'bytes')
        }
      }
      
      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder stopped, processing audio...')
        setIsListening(false)
        
        // Only process if not stopped by user action (mute button)
        if (!isStoppedByUserRef.current && !isCleaningUpRef.current) {
          setIsProcessing(true)
          
          try {
            // Check if we have enough audio data
            if (audioChunksRef.current.length === 0) {
              console.log('No audio data recorded')
              setError('No audio recorded. Please try speaking louder.')
              return
            }
            
            const audioBlob = new Blob(audioChunksRef.current, { 
              type: mediaRecorder.mimeType || 'audio/webm' 
            })
            
            console.log('Audio blob created:', audioBlob.size, 'bytes, type:', audioBlob.type)
            
            // Minimum recording time and size check
            const recordingDuration = Date.now() - recordingStartTimeRef.current
            if (audioBlob.size < 2048) { // Increased minimum size
              console.log('Recording too small, ignoring...')
              setError('Recording too short. Please speak for longer.')
              return
            }
            
            if (recordingDuration < 800) { // Increased minimum duration
              console.log('Recording too short, ignoring...')
              setError('Recording too short. Please speak for longer.')
              return
            }
            
            // Process the audio
            console.log('Processing audio for transcription...')
            const transcription = await processVoiceInput(audioBlob)
            
            if (transcription && transcription.trim()) {
              console.log('Transcription successful:', transcription)
              setTranscribedText(transcription)
              setError(null)
            } else {
              console.log('No transcription result')
              setError('Could not understand speech. Please try again.')
            }
            
          } catch (error) {
            console.error('Error processing recording:', error)
            setError('Error processing voice input: ' + error.message)
          } finally {
            setIsProcessing(false)
          }
        }
        
        // Cleanup audio context and stream
        cleanup()
      }
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error)
        setError('Recording error: ' + (event.error?.message || 'Unknown error'))
        setIsListening(false)
        setIsProcessing(false)
        cleanup()
      }
      
      // Start recording with smaller time slices for better responsiveness
      console.log('Starting MediaRecorder...')
      mediaRecorder.start(250) // Collect data every 250ms
      setIsListening(true)
      
      // Start voice detection
      startVoiceDetection()
      
    } catch (error) {
      console.error('Error starting voice recording:', error)
      
      let errorMessage = 'Failed to start voice recording'
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions.'
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone.'
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Voice recording not supported in this browser.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
      setIsListening(false)
      setIsProcessing(false)
      cleanup()
    }
  }, [isListening, isProcessing, isSpeaking, startVoiceDetection, cleanup])
  
  // Stop listening
  const stopListening = useCallback((userInitiated = false) => {
    console.log('🛑 Stopping voice recording...', userInitiated ? '(user initiated)' : '(auto)')
    
    // Set flag if user initiated (like mute button)
    if (userInitiated) {
      isStoppedByUserRef.current = true
    }
    
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      
      // Clear timers
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      
      setVoiceLevel(0)
    } catch (error) {
      console.error('Error stopping recording:', error)
      setError('Error stopping recording')
    }
  }, [])
  
  // Process voice input using ElevenLabs with better error handling
  const processVoiceInput = useCallback(async (audioBlob) => {
    console.log('Processing voice input with ElevenLabs...')
    console.log('API Key available:', !!elevenLabsApiKey && elevenLabsApiKey !== 'your_elevenlabs_api_key_here')
    
    if (!elevenLabsApiKey || elevenLabsApiKey === 'your_elevenlabs_api_key_here') {
      console.log('No ElevenLabs API key, using simulation...')
      
      // Enhanced simulation
      const simulatedTexts = {
        english: [
          "I'm feeling a bit overwhelmed today",
          "Can you help me process my thoughts?",
          "I had a difficult conversation with my friend",
          "I'm struggling with work-life balance",
          "How can I manage my anxiety better?",
          "I need some clarity about this situation",
          "What do you think about this?"
        ],
        tagalog: [
          "Medyo nalilito ako ngayon",
          "Pwede mo ba akong tulungan sa problema ko?",
          "Nahihirapan ako sa trabaho",
          "Paano ko ba masosolusyunan ito?",
          "Kailangan ko ng advice"
        ],
        spanish: [
          "Me siento un poco abrumado hoy",
          "¿Puedes ayudarme a procesar mis pensamientos?",
          "Tuve una conversación difícil",
          "Estoy luchando con el equilibrio",
          "¿Cómo puedo manejar mejor mi ansiedad?"
        ]
      }
      
      const textsForLanguage = simulatedTexts[detectedLanguage] || simulatedTexts.english
      const randomText = textsForLanguage[Math.floor(Math.random() * textsForLanguage.length)]
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 800)) // Reduced from 1200ms
      return randomText
    }

    try {
      // Convert to proper format for ElevenLabs
      const formData = new FormData()
      
      // Create a proper file with correct extension based on blob type
      let fileName = 'audio.webm'
      if (audioBlob.type.includes('mp4')) {
        fileName = 'audio.mp4'
      } else if (audioBlob.type.includes('wav')) {
        fileName = 'audio.wav'
      } else if (audioBlob.type.includes('mpeg')) {
        fileName = 'audio.mp3'
      }
      
      const audioFile = new File([audioBlob], fileName, { type: audioBlob.type })
      
      formData.append('file', audioFile)
      formData.append('model_id', 'whisper-1') // Use Whisper model for better accuracy
      
      // Add language if not English
      if (detectedLanguage !== 'english') {
        const languageMap = {
          'tagalog': 'tl',
          'spanish': 'es',
          'french': 'fr',
          'german': 'de',
          'italian': 'it',
          'portuguese': 'pt',
          'japanese': 'ja',
          'korean': 'ko',
          'chinese': 'zh'
        }
        const speechLang = languageMap[detectedLanguage] || 'en'
        formData.append('language', speechLang)
      }

      console.log('Sending request to ElevenLabs Speech-to-Text API...')
      const response = await fetch('https://api.elevenlabs.io/v1/speech-to-text', {
        method: 'POST',
        headers: {
          'xi-api-key': elevenLabsApiKey
        },
        body: formData
      })

      console.log('ElevenLabs response status:', response.status)

      if (response.ok) {
        const result = await response.json()
        console.log('ElevenLabs response:', result)
        
        if (!result.text || result.text.trim().length === 0) {
          console.log('Empty transcription result')
          throw new Error('No speech detected in audio')
        }
        
        return result.text.trim()
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('ElevenLabs Speech-to-Text error:', response.status, response.statusText, errorData)
        
        if (response.status === 401) {
          throw new Error('Invalid ElevenLabs API key')
        } else if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.')
        } else if (response.status === 413) {
          throw new Error('Audio file too large')
        } else {
          throw new Error(`Speech-to-text service error: ${response.status}`)
        }
      }
    } catch (error) {
      console.error('Error processing voice input:', error)
      throw error
    }
  }, [elevenLabsApiKey, detectedLanguage])
  
  // Text-to-speech with ElevenLabs using default settings
  const speak = useCallback(async (text) => {
    if (!settings.voiceEnabled || !text) return

    console.log('🗣️ Speaking text with default voice settings:', text)

    // CRITICAL: Stop any currently playing audio to prevent echo
    console.log('Stopping any existing audio before speaking...')
    if (currentAudioRef.current) {
      currentAudioRef.current.pause()
      currentAudioRef.current.currentTime = 0
      currentAudioRef.current = null
    }
    
    // Stop browser speech synthesis
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel()
      if (speechSynthesisUtteranceRef.current) {
        speechSynthesisUtteranceRef.current.onend = null
        speechSynthesisUtteranceRef.current.onerror = null
        speechSynthesisUtteranceRef.current = null
      }
    }

    if (elevenLabsApiKey && elevenLabsApiKey !== 'your_elevenlabs_api_key_here') {
      try {
        setIsSpeaking(true)
        
        // Use Alexandra voice ID for all languages
        const alexandraVoiceId = 'kdmDKE6EkgrWrrykO9Qt'
        let modelId = 'eleven_turbo_v2_5' // Use the latest turbo model for faster generation
        
        // For non-English languages, use the multilingual model
        if (detectedLanguage !== 'english') {
          modelId = 'eleven_multilingual_v2'
        }
        
        console.log(`Using Alexandra voice (${alexandraVoiceId}) with model ${modelId} for language: ${detectedLanguage}`)
        
        const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${alexandraVoiceId}`, {
          method: 'POST',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json',
            'xi-api-key': elevenLabsApiKey
          },
          body: JSON.stringify({
            text: text,
            model_id: modelId,
            voice_settings: {
              stability: 0.5,           // Default stability
              similarity_boost: 0.8,    // High similarity to maintain Alexandra's voice
              style: 0.0,              // Default style
              use_speaker_boost: true,
              speed: 1.0               // Default speed (natural pace)
            },
            output_format: "mp3_44100_128" // Good quality format
          })
        })
        
        if (response.ok) {
          const audioBlob = await response.blob()
          const audioUrl = URL.createObjectURL(audioBlob)
          const audio = new Audio(audioUrl)
          currentAudioRef.current = audio
          
          // Default audio playback settings
          audio.playbackRate = 1.0 // Default speed
          audio.preload = 'auto'
          
          audio.onloadeddata = () => {
            console.log('Audio loaded, starting playback')
          }
          
          audio.onended = () => {
            console.log('✅ Alexandra finished speaking')
            setIsSpeaking(false)
            URL.revokeObjectURL(audioUrl)
            currentAudioRef.current = null
          }
          
          audio.onerror = (e) => {
            console.error('Audio playback error:', e)
            setIsSpeaking(false)
            URL.revokeObjectURL(audioUrl)
            currentAudioRef.current = null
            fallbackToSpeechSynthesis(text)
          }
          
          // Start playback immediately
          try {
            await audio.play()
            console.log('Audio playback started successfully')
          } catch (playError) {
            console.error('Error starting audio playback:', playError)
            fallbackToSpeechSynthesis(text)
          }
          return
        } else {
          console.error('ElevenLabs TTS error:', response.status)
          const errorData = await response.json().catch(() => ({}))
          console.error('Error details:', errorData)
          fallbackToSpeechSynthesis(text)
        }
      } catch (error) {
        console.error('Error with ElevenLabs TTS:', error)
        fallbackToSpeechSynthesis(text)
      }
    } else {
      fallbackToSpeechSynthesis(text)
    }
    
    function fallbackToSpeechSynthesis(text) {
      try {
        console.log('🔄 Using browser speech synthesis as fallback')
        setIsSpeaking(true)
        
        const utterance = new SpeechSynthesisUtterance(text)
        speechSynthesisUtteranceRef.current = utterance
        
        // Default browser speech settings
        utterance.rate = 1.0     // Default rate
        utterance.pitch = 1.0    // Normal pitch
        utterance.volume = 0.8
        
        const voices = window.speechSynthesis.getVoices()
        let preferredVoice = voices.find(voice => 
          voice.name.includes('Female') || 
          voice.name.includes('Samantha') ||
          voice.name.includes('Google') ||
          voice.lang.includes('en')
        )
        
        if (preferredVoice) {
          utterance.voice = preferredVoice
          console.log('Using voice:', preferredVoice.name)
        }
        
        utterance.onstart = () => {
          console.log('Browser speech started')
        }
        
        utterance.onend = () => {
          console.log('✅ Browser speech finished')
          setIsSpeaking(false)
          speechSynthesisUtteranceRef.current = null
        }
        
        utterance.onerror = (e) => {
          console.error('Browser speech error:', e)
          setIsSpeaking(false)
          speechSynthesisUtteranceRef.current = null
        }
        
        window.speechSynthesis.speak(utterance)
      } catch (error) {
        console.error('Error with browser speech:', error)
        setIsSpeaking(false)
        speechSynthesisUtteranceRef.current = null
      }
    }
  }, [elevenLabsApiKey, settings.voiceEnabled, detectedLanguage])
  
  // Stop speaking
  const stopSpeaking = useCallback(() => {
    console.log('🛑 Stopping speech...')
    
    // Stop ElevenLabs audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause()
      currentAudioRef.current.currentTime = 0
      currentAudioRef.current = null
    }
    
    // Stop browser speech synthesis
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel()
    }
    
    if (speechSynthesisUtteranceRef.current) {
      speechSynthesisUtteranceRef.current.onend = null
      speechSynthesisUtteranceRef.current.onerror = null
      speechSynthesisUtteranceRef.current = null
    }
    
    setIsSpeaking(false)
  }, [])
  
  // Get transcribed text and clear it
  const getTranscribedText = useCallback(() => {
    const text = transcribedText
    setTranscribedText('')
    console.log('Getting transcribed text:', text)
    return text
  }, [transcribedText])
  
  // Cleanup on unmount and expose stopAllVoiceActivities for external use
  useEffect(() => {
    return () => {
      console.log('🧹 useVoiceChat unmounting, cleaning up...')
      cleanup()
      stopAllVoiceActivities()
    }
  }, [cleanup, stopAllVoiceActivities])
  
  return {
    isListening,
    isSpeaking,
    isProcessing,
    transcribedText,
    voiceLevel,
    error, // Export error state
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    getTranscribedText,
    stopAllVoiceActivities // Export this for external use
  }
}